import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { todayIso, dayIso } from '@/lib/today'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, CalendarClock, Clock3, Columns3, FileText, MapPin, Package, Plus, SlidersHorizontal, UserRound, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useClients, clientName } from '@/hooks/use-clients'
import { useCompany, useTeam } from '@/hooks/use-company'
import { useAuth } from '@/hooks/use-auth'
import { entryMinutes, fmtHours, useJobTime } from '@/hooks/use-time'
import { useTaskMaterials } from '@/hooks/use-inventory'
import { useJobNotifications } from '@/hooks/use-notifications'
import { JOB_STATUSES } from '@/hooks/use-job-form'
import { Page, PageHeader, Card, Table, Trow, StatusPill, TabPills, Pagination, SearchInput, SkeletonRows, EmptyState, Toolbar, Workspace, btnPrimary, btnGhost, btnSecondary } from '@/components/rm'
import { Select } from '@/components/rm/form'
import { phFormat, manilaWallToIso } from '@/lib/ph'
import {
  BASE_COLUMN, BUILTIN_COLUMNS, DEFAULT_KEYS, clearColumnKeys, gridTemplate,
  loadColumnKeys, resolveColumns, saveColumnKeys, type JobColumn,
} from '@/lib/jobs-columns'

export const Route = createFileRoute('/_app/jobs')({ component: JobsPage })

const PAGE = 15
const UNASSIGNED = '__none__'
const CLOSED = ['Completed', 'Completed-Follow-Up', 'Cancelled', 'Closed', 'Paid']
const fmtDate = (iso: string | null) => (iso ? phFormat(new Date(iso), 'date', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—')
/**
 * The window a customer are used to: two weeks either side of today, Pending only.
 * Matching RouteMate Plus deliberately — people migrating mid-job shouldn't have to relearn
 * where their work went, and an empty-looking Jobs list reads as lost data.
 */
function defaultRange() {
  const iso = (d: Date) => {
    const x = new Date(d)
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
  }
  const now = new Date()
  const from = new Date(now); from.setDate(now.getDate() - 14)
  const to = new Date(now); to.setDate(now.getDate() + 14)
  return { from: iso(from), to: iso(to) }
}

function weekRange() {
  const now = new Date(); const day = (now.getDay() + 6) % 7
  const mon = new Date(now); mon.setDate(now.getDate() - day)
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6)
  return { from: dayIso(mon), to: dayIso(sun) }
}

export interface JobRow {
  id: number; name: string | null; status: string | null; user_id: string | null; contact_id: string | null
  address: string | null; line1: string | null; line2: string | null; city: string | null; state: string | null; postal_code: string | null
  scheduled_date: string | null; finishes?: string | null; created_at?: string | null
  task_field_value?: Array<{ field_id: string; value_text: string | null }> | null
}
/** Migrated jobs keep the street in line1 with `address` empty — read whatever exists. */
const rowAddress = (j: JobRow) => {
  const street = [j.line1, j.line2].filter(Boolean).join(' ').trim()
  const region = [j.city, j.state, j.postal_code].filter(Boolean).join(' ').trim()
  if (street) return [street, region].filter(Boolean).join(', ')
  return (j.address ?? '').trim() || region
}

type DateFilterMode = 'default' | 'custom' | 'none'

type JobsWorkspaceState = {
  search: string
  quick: string
  status: string
  contactId: string
  userId: string
  from: string
  to: string
  dateFilterMode: DateFilterMode
  page: number
  scrollTop: number
}

const JOBS_WORKSPACE_STATE_KEY = 'myserbisyo.jobs-workspace.v1'

function loadJobsWorkspaceState(): Partial<JobsWorkspaceState> {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(JOBS_WORKSPACE_STATE_KEY) ?? '{}') as Partial<JobsWorkspaceState>
    return {
      search: typeof parsed.search === 'string' ? parsed.search : '',
      quick: typeof parsed.quick === 'string' ? parsed.quick : 'all',
      status: typeof parsed.status === 'string' ? parsed.status : '',
      contactId: typeof parsed.contactId === 'string' ? parsed.contactId : '',
      userId: typeof parsed.userId === 'string' ? parsed.userId : '',
      from: typeof parsed.from === 'string' ? parsed.from : undefined,
      to: typeof parsed.to === 'string' ? parsed.to : undefined,
      dateFilterMode: parsed.dateFilterMode === 'custom' || parsed.dateFilterMode === 'none' || parsed.dateFilterMode === 'default' ? parsed.dateFilterMode : undefined,
      page: Number.isInteger(parsed.page) && (parsed.page ?? 0) >= 0 ? parsed.page : 0,
      scrollTop: typeof parsed.scrollTop === 'number' && parsed.scrollTop >= 0 ? parsed.scrollTop : 0,
    }
  } catch {
    return {}
  }
}

function saveJobsWorkspaceState(state: JobsWorkspaceState) {
  try { window.sessionStorage.setItem(JOBS_WORKSPACE_STATE_KEY, JSON.stringify(state)) } catch { /* storage can be unavailable in private browsing */ }
}

function JobsPage() {
  return <JobsWorkspacePage />
}

/**
 * Shared wide-screen workspace for both /jobs and the durable /jobs/:id route.
 * Filter, page and rail scroll state live in session storage so a route transition keeps the
 * operator's queue context while TanStack Router swaps the selected job URL.
 */
export function JobsWorkspacePage({ selectedJobId }: { selectedJobId?: number } = {}) {
  const navigate = useNavigate()
  const { clients } = useClients()
  const { members } = useTeam()
  const { user, companyId, role } = useAuth()
  const { company } = useCompany()
  /**
   * Who this viewer can actually be shown jobs for. RLS lets a non-admin see only their own jobs
   * and unassigned ones unless the company has opened it up, so offering the rest of the crew in
   * "Assigned to" was offering a filter that silently returns nothing — which is exactly what
   * A customer reported: an admin picked a crew member and got no results.
   */
  const seesEveryone = role === 'company-admin' || !!company?.crew_can_view_all_jobs

  const restoredState = useMemo(loadJobsWorkspaceState, [])
  const defaultDates = useMemo(defaultRange, [])
  // A new (or legacy) Jobs workspace starts with the useful ±14-day queue. Once an operator
  // explicitly clears or edits dates, that choice is their workspace context and must survive
  // route changes and reloads—otherwise the Clear action visibly undoes itself.
  const initialDateFilterMode: DateFilterMode = restoredState.dateFilterMode ?? 'default'
  const [search, setSearch] = useState(restoredState.search ?? '')
  const [quick, setQuick] = useState(restoredState.quick ?? 'all')
  // No status default. The date window below is a starting point people change; a status filter on
  // top of it is a constraint they cannot see, and it silently answered a different question than
  // the one they asked. A customer has 2,686 jobs of which 28 are Pending — pick any past month and
  // 'Pending' returns nothing while the month is full of completed work. (Owner, 2026-08-05: the
  // ±14 days "was just a default value and can be changed... we don't want to limit the function".)
  const [status, setStatus] = useState(restoredState.status ?? '')
  const [contactId, setContactId] = useState(restoredState.contactId ?? '')
  const [userId, setUserId] = useState(restoredState.userId ?? '')
  const [dateFilterMode, setDateFilterMode] = useState<DateFilterMode>(initialDateFilterMode)
  const [from, setFrom] = useState(() => initialDateFilterMode === 'default' ? defaultDates.from : restoredState.from ?? '')
  const [to, setTo] = useState(() => initialDateFilterMode === 'default' ? defaultDates.to : restoredState.to ?? '')
  const [page, setPage] = useState(restoredState.page ?? 0)
  const [showColumns, setShowColumns] = useState(false)
  const [localSelectedId, setLocalSelectedId] = useState<number | null>(null)
  const [previewTab, setPreviewTab] = useState('overview')
  const [railCollapsed, setRailCollapsed] = useState(false)
  const listScrollerRef = useRef<HTMLElement>(null)
  const selectedId = selectedJobId ?? localSelectedId
  // null = never customised, so this user follows the default. Read once; writes go to both.
  const [colKeys, setColKeys] = useState<string[] | null>(() => loadColumnKeys(user?.id))

  // The company's own job-form fields, offered as columns. "Job Description" and "Technician
  // Notes" ship in the default form template, so every company has at least those two.
  const customFields = useQuery({
    queryKey: ['job-column-fields', companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<JobColumn[]> => {
      // The PRIMARY form only. A company can have a dozen forms — a customer have SWMS, Take 5 and
      // four compliance certificates — but all 2,687 of their jobs sit on the primary "Job/Task"
      // form, and every other form has zero jobs. Offering every field would bury the two columns
      // they actually want under 57 they do not.
      const { data, error } = await supabase
        .from('custom_form_field')
        .select('id, name, type, row, hidden, custom_form!inner(company_id, is_primary)')
        .eq('custom_form.company_id', companyId!)
        .eq('custom_form.is_primary', true)
        .is('deleted_at', null)
        .order('row', { ascending: true })
      if (error) throw error
      const rows = (data ?? []) as Array<{ id: string; name: string; type: string; hidden: boolean | null }>
      return rows
        // Headers and signatures are not values; images and files cannot be a table cell. Hidden
        // fields are hidden on purpose, so they are not offered as columns either.
        .filter((f) => !f.hidden && !['header', 'signature', 'image', 'file'].includes(f.type))
        .map((f) => ({ key: `cf:${f.id}`, label: f.name, width: '1fr', fieldId: f.id }))
    },
  })

  const columns = useMemo(
    () => resolveColumns(colKeys, customFields.data ?? []),
    [colKeys, customFields.data],
  )
  const needsFieldValues = columns.some((c) => c.fieldId)
  const gridCols = gridTemplate(columns)

  const setColumns = (keys: string[]) => {
    setColKeys(keys)
    saveColumnKeys(user?.id, keys)
  }
  const resetColumns = () => {
    setColKeys(null)
    clearColumnKeys(user?.id)
  }
  // Open by default: the list now arrives pre-filtered (today ±14 days). A filter you
  // can't see is indistinguishable from missing data — which is exactly the panic we're avoiding.
  const [showFilters, setShowFilters] = useState(true)
  const reset = () => setPage(0)

  const contactName = (cid: string | null) => { if (!cid) return '—'; const c = clients.find((x) => x.id === cid); return c ? clientName(c) : '—' }
  const userName = (uid: string | null) => { if (!uid) return '—'; const u = members.find((x) => x.id === uid); return u ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—' : '—' }
  const activeCount = [status, contactId, userId, from, to].filter(Boolean).length
  const hasActiveListFilters = activeCount > 0 || Boolean(search) || quick !== 'all'
  // Clear applies to the view that is open now and remains cleared until the operator chooses a
  // date range again. A new workspace still starts with the default range above.
  const clearAll = () => { setStatus(''); setContactId(''); setUserId(''); setDateFilterMode('none'); setFrom(''); setTo(''); setSearch(''); setQuick('all'); reset() }

  // Tab counts (cheap head queries, independent of the page).
  const counts = useQuery({
    queryKey: ['jobs', 'counts'],
    queryFn: async () => {
      const head = { count: 'exact' as const, head: true }
      const closedList = `(${CLOSED.join(',')})`
      const [all, openc, noClient] = await Promise.all([
        supabase.from('task').select('id', head).eq('is_draft', false).is('deleted_at', null),
        supabase.from('task').select('id', head).eq('is_draft', false).is('deleted_at', null).or(`status.is.null,status.not.in.${closedList}`),
        supabase.from('task').select('id', head).eq('is_draft', false).is('deleted_at', null).is('contact_id', null),
      ])
      const total = all.count ?? 0, open = openc.count ?? 0
      return { all: total, open, done: total - open, noClient: noClient.count ?? 0 }
    },
  })

  // Server-side filtered + paginated page.
  // Spec 036. Per-user, newest first, and independent of the filters — a draft hidden by a date
  // filter is a lost draft, which is the whole thing this feature exists to prevent.
  const drafts = useQuery({
    queryKey: ['job-drafts'],
    queryFn: async () => {
      const uid = (await supabase.auth.getUser()).data.user?.id
      if (!uid) return []
      const { data, error } = await supabase.from('task')
        .select('id, name, address, line1, line2, city, state, postal_code, draft_updated_at')
        .eq('is_draft', true).eq('user_id', uid).is('deleted_at', null)
        .order('draft_updated_at', { ascending: false, nullsFirst: false })
        .limit(20)
      if (error) throw error
      return (data ?? []) as Array<JobRow & { draft_updated_at: string | null }>
    },
  })

  const jobs = useQuery({
    queryKey: ['jobs', 'page', { search, quick, status, contactId, userId, from, to, page, needsFieldValues }],
    queryFn: async () => {
      // Search goes through jobs_search() rather than .from('task') because it has to reach the
      // CLIENT's name, and an `or=` across two tables cannot be expressed from here. The function
      // returns SETOF task, so every filter, order and range below still applies on top of it
      // exactly as it did against the table — only the search term moves into SQL.
      let q = supabase
        .rpc('jobs_search', { q: search.trim() }, { count: 'exact' })
        // The field values ride along ONLY when a custom column is on screen. Everyone still on
        // the default columns pays nothing for a feature they have not turned on.
        .select('id, name, status, user_id, contact_id, address, line1, line2, city, state, postal_code, '
          + 'scheduled_date:planned_start_date, finishes:planned_end_date, created_at'
          + (needsFieldValues ? ', task_field_value(field_id, value_text)' : ''))
        .eq('is_draft', false)
        .is('deleted_at', null)
      if (quick === 'open') q = q.or(`status.is.null,status.not.in.(${CLOSED.join(',')})`)
      else if (quick === 'done') q = q.in('status', CLOSED)
      else if (quick === 'no_client') q = q.is('contact_id', null)
      if (status) q = q.eq('status', status)
      if (contactId) q = q.eq('contact_id', contactId)
      if (userId === UNASSIGNED) q = q.is('user_id', null)
      else if (userId) q = q.eq('user_id', userId)
      // Local day -> UTC instants. planned_start_date is stored in UTC, so comparing it to a
      // naive "yyyy-MM-dd" asks for a UTC day — in Brisbane that runs 10am to 10am, which both
      // hides early-morning jobs and pulls in the next day's.
      if (from) q = q.gte('planned_start_date', manilaWallToIso(from, '00:00:00')!)
      if (to) q = q.lte('planned_start_date', manilaWallToIso(to, '23:59:59.999')!)
      // Soonest first. Ordering by created_at showed a date-filtered list in apparently random
      // order, because when a job was entered has nothing to do with when it is scheduled.
      const { data, error, count } = await q.order('planned_start_date', { ascending: true, nullsFirst: false })
        // Tie-break: 456 of a customer's jobs share a start time, and tied rows in arbitrary order
        // mean a job on a page boundary can appear twice or be skipped entirely.
        .order('id', { ascending: true })
        .range(page * PAGE, page * PAGE + PAGE - 1)
      if (error) throw error
      // Cast through unknown: the select string is built at runtime (the field values only ride
      // along when a custom column is on), so supabase-js cannot infer the row shape from it.
      return { rows: (data ?? []) as unknown as JobRow[], count: count ?? 0 }
    },
  })
  const rows = jobs.data?.rows ?? []
  const c = counts.data

  /**
   * Preselect the first job so the workspace opens with something to read. An empty right pane
   * wastes half the screen and costs the operator a click before they learn anything.
   *
   * Three deliberate limits:
   * - Only where the preview is on screen. The rail layout starts at 1200px; below that the
   *   mobile list is shown, and a selection there would jump straight into a job record.
   * - Never over an existing choice, whether it came from the URL or from a click.
   * - Rows only, never a draft. A draft row opens the edit form, so preselecting one would drop
   *   somebody into a form on arrival.
   *
   * This sets local state rather than navigating, so arriving at /jobs does not push a job URL
   * into history and turn Back into a no-op.
   */
  const firstRowId = rows[0]?.id ?? null
  useEffect(() => {
    if (selectedJobId != null || localSelectedId !== null || firstRowId === null) return
    if (!window.matchMedia('(min-width: 1200px)').matches) return
    setLocalSelectedId(firstRowId)
  }, [selectedJobId, localSelectedId, firstRowId])

  const selectedJobIsOutsideList = selectedId !== null && !rows.some((job) => job.id === selectedId)
  const selectedJobQuery = useQuery({
    queryKey: ['job-workspace-selected', selectedId],
    enabled: selectedJobIsOutsideList,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task')
        .select('id, name, status, user_id, contact_id, address, line1, line2, city, state, postal_code, scheduled_date:planned_start_date, finishes:planned_end_date, created_at')
        .eq('id', selectedId!)
        .is('deleted_at', null)
        .maybeSingle()
      if (error) throw error
      return data as JobRow | null
    },
  })
  const selectedJob = rows.find((job) => job.id === selectedId) ?? selectedJobQuery.data ?? null

  useEffect(() => {
    saveJobsWorkspaceState({ search, quick, status, contactId, userId, from, to, dateFilterMode, page, scrollTop: listScrollerRef.current?.scrollTop ?? restoredState.scrollTop ?? 0 })
  }, [search, quick, status, contactId, userId, from, to, dateFilterMode, page, restoredState.scrollTop])

  useEffect(() => {
    const list = listScrollerRef.current
    if (list && restoredState.scrollTop && list.scrollTop === 0) list.scrollTop = restoredState.scrollTop
  }, [restoredState.scrollTop])

  const persistListContext = () => saveJobsWorkspaceState({
    search, quick, status, contactId, userId, from, to, dateFilterMode, page,
    scrollTop: listScrollerRef.current?.scrollTop ?? 0,
  })
  const selectJob = (id: number) => {
    persistListContext()
    setPreviewTab('overview')
    setLocalSelectedId(id)
    navigate({ to: '/jobs/$id', params: { id: String(id) } })
  }

  return (
    <Page>
      <PageHeader
        title="Jobs"
        subtitle={`${jobs.data?.count ?? 0} jobs in this view`}
        actions={<button className={btnPrimary} onClick={() => navigate({ to: '/jobs/new', search: { contact: undefined } })}><Plus className="h-4 w-4" /> New job</button>}
      />
      <div className="mb-3 mt-5 flex items-center justify-between gap-3">
        <TabPills active={quick} onChange={(v) => { setQuick(v); reset() }} chips={[
          { id: 'all', label: 'All', count: c?.all },
          { id: 'open', label: 'Open', count: c?.open },
          { id: 'done', label: 'Done', count: c?.done },
          { id: 'no_client', label: 'No client', count: c?.noClient },
        ]} />
      </div>

      <Toolbar className="mb-3">
        <SearchInput value={search} onChange={(v) => { setSearch(v); reset() }} placeholder="Job or address…" />
        {[
          { label: 'My jobs', on: () => { clearAll(); setQuick('open'); if (user?.id) setUserId(user.id) } },
          { label: 'This week', on: () => { clearAll(); const w = weekRange(); setDateFilterMode('custom'); setFrom(w.from); setTo(w.to) } },
          { label: 'Overdue', on: () => { clearAll(); setQuick('open'); setDateFilterMode('custom'); setTo(todayIso()) } },
          { label: 'Unassigned', on: () => { clearAll(); setUserId(UNASSIGNED) } },
        ].map((p) => (
          <button key={p.label} onClick={p.on} className="min-h-8 rounded-full border border-token bg-token-surface px-3 py-1 text-[12.5px] font-semibold text-token-secondary transition-colors hover:border-token-strong hover:text-token-primary [@media(pointer:coarse)]:min-h-[44px]">{p.label}</button>
        ))}
        <button onClick={() => setShowFilters((v) => !v)}
          className={`inline-flex h-8 items-center gap-1.5 rounded-[9px] border px-3 text-[12.5px] font-semibold transition-colors [@media(pointer:coarse)]:min-h-[44px] ${showFilters || activeCount > 0 ? 'border-token-strong bg-brand text-brand-fg' : 'border-token bg-token-surface text-token-secondary hover:border-token-strong hover:text-token-primary'}`}>
          <SlidersHorizontal className="h-3.5 w-3.5" /> Filters{activeCount > 0 && <span className="flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-accent px-1 text-[10.5px] font-bold text-token-primary tabular-nums">{activeCount}</span>}
        </button>
        <button className={btnGhost} onClick={() => setShowColumns((v) => !v)} aria-expanded={showColumns}>
          <Columns3 className="h-3.5 w-3.5" /> Columns
        </button>
        {(activeCount > 0 || search || quick !== 'all') && <button className={btnGhost} onClick={clearAll}><X className="h-3.5 w-3.5" /> Clear</button>}
      </Toolbar>

      {showFilters && (
        <Card className="mb-4">
          <div className="flex flex-wrap items-end gap-3 p-3.5">
            <FilterField label="Status"><Select value={status} onChange={(e) => { setStatus(e.target.value); reset() }} className="h-9 w-[160px]"><option value="">Any status</option>{JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></FilterField>
            <FilterField label="Client"><Select value={contactId} onChange={(e) => { setContactId(e.target.value); reset() }} className="h-9 w-[180px]"><option value="">Any client</option>{clients.map((cl) => <option key={cl.id} value={cl.id}>{clientName(cl)}</option>)}</Select></FilterField>
            <FilterField label="Assigned"><Select value={userId} onChange={(e) => { setUserId(e.target.value); reset() }} className="h-9 w-[160px]"><option value="">Anyone</option><option value={UNASSIGNED}>Unassigned</option>{(seesEveryone ? members : members.filter((m) => m.id === user?.id)).map((m) => <option key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}</option>)}</Select></FilterField>
            <FilterField label="From"><input type="date" value={from} onChange={(e) => { setDateFilterMode('custom'); setFrom(e.target.value); reset() }} className="h-9 rounded-[9px] border border-token bg-token-surface px-2.5 text-[13px] text-token-primary outline-none focus:border-token-strong [@media(pointer:coarse)]:h-11" /></FilterField>
            <FilterField label="To"><input type="date" value={to} onChange={(e) => { setDateFilterMode('custom'); setTo(e.target.value); reset() }} className="h-9 rounded-[9px] border border-token bg-token-surface px-2.5 text-[13px] text-token-primary outline-none focus:border-token-strong [@media(pointer:coarse)]:h-11" /></FilterField>
          </div>
        </Card>
      )}

      {showColumns && (
        <ColumnPicker
          columns={columns}
          custom={customFields.data ?? []}
          onChange={setColumns}
          onReset={resetColumns}
          onClose={() => setShowColumns(false)}
        />
      )}

      <div className="hidden min-[1200px]:block">
        <Workspace
          railCollapsed={railCollapsed}
          listRef={listScrollerRef}
          onListScroll={persistListContext}
          list={
            <div className="flex min-h-full flex-col">
              <div className="flex items-center justify-between border-b border-token-subtle px-4 py-3">
                <div>
                  <p className="font-display text-[15px] font-semibold text-token-primary">Jobs</p>
                  <p className="mt-0.5 text-[12px] text-token-secondary">{jobs.data?.count ?? 0} matching this view</p>
                </div>
              </div>
              {selectedJobIsOutsideList && selectedJob && (
                <div className="border-b border-token-subtle bg-canvas-subtle px-4 py-2.5 text-[12px] text-token-secondary">
                  {hasActiveListFilters ? (
                    <>This job is outside the current filters. <button className="font-semibold text-brand underline underline-offset-2" onClick={clearAll}>Clear filters</button></>
                  ) : (
                    <>This job is not on this page of the queue.</>
                  )}
                </div>
              )}
              {jobs.isLoading ? (
                <SkeletonRows count={9} />
              ) : jobs.isError ? (
                <JobListError onRetry={() => jobs.refetch()} />
              ) : rows.length === 0 ? (
                <EmptyState label={(c?.all ?? 0) === 0 ? 'No jobs yet.' : 'No jobs match these filters.'} cta={(c?.all ?? 0) === 0 ? 'New job' : undefined} onCta={() => navigate({ to: '/jobs/new', search: { contact: undefined } })} />
              ) : (
                <div className="divide-y divide-token-subtle">
                  {(drafts.data ?? []).map((draft) => (
                    <button key={`draft-${draft.id}`} onClick={() => navigate({ to: '/jobs/$id/edit', params: { id: String(draft.id) } })} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-token-hover">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-semibold text-token-primary">{draft.name || 'Untitled job'}</span>
                        <span className="mt-0.5 block truncate text-[12px] text-token-secondary">Draft · {rowAddress(draft) || 'No address yet'}</span>
                      </span>
                      <span className="rounded-[6px] bg-token-hover px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em] text-token-secondary">Draft</span>
                    </button>
                  ))}
                  {rows.map((job) => {
                    const selected = job.id === selectedId
                    return (
                      <button
                        key={job.id}
                        onClick={() => selectJob(job.id)}
                        aria-current={selected ? 'page' : undefined}
                        className={`relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${selected ? 'bg-token-selected' : 'hover:bg-token-hover'}`}
                      >
                        {selected && <span className="absolute inset-y-0 left-0 w-[3px] bg-brand" />}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13.5px] font-semibold text-token-primary">{job.name || 'Untitled job'}</span>
                          <span className="mt-0.5 block truncate text-[12px] text-token-secondary">{contactName(job.contact_id) !== '—' ? contactName(job.contact_id) : rowAddress(job) || 'No client or address'}</span>
                          <span className="mt-1 block truncate text-[11px] text-token-muted">{fmtDate(job.scheduled_date)}{job.user_id ? ` · ${userName(job.user_id)}` : ' · Unassigned'}</span>
                        </span>
                        <StatusPill status={job.status || 'pending'} />
                      </button>
                    )
                  })}
                </div>
              )}
              <div className="mt-auto border-t border-token-subtle">
                <Pagination page={page} pageSize={PAGE} total={jobs.data?.count ?? 0} onPage={(next) => { setLocalSelectedId(null); setPage(next) }} />
              </div>
            </div>
          }
          inspector={
            <JobWorkspacePreview
              job={selectedJob}
              clientName={contactName}
              userName={userName}
              tab={previewTab}
              onTab={setPreviewTab}
              railCollapsed={railCollapsed}
              onToggleRail={() => setRailCollapsed((collapsed) => !collapsed)}
              isLoading={selectedJobQuery.isLoading}
              isError={selectedJobQuery.isError}
              hasSelection={selectedId !== null}
              onRetry={() => selectedJobQuery.refetch()}
              onOpen={() => selectedJob && navigate({ to: '/jobs/$id', params: { id: String(selectedJob.id) }, search: { view: 'record' } })}
              onEdit={() => selectedJob && navigate({ to: '/jobs/$id/edit', params: { id: String(selectedJob.id) } })}
            />
          }
        />
      </div>

      <div className="min-[1200px]:hidden">
      {jobs.isLoading ? (
        <Card><SkeletonRows count={8} /></Card>
      ) : jobs.isError ? (
        <Card><JobListError onRetry={() => jobs.refetch()} /></Card>
      ) : rows.length === 0 ? (
        <Card><EmptyState label={(c?.all ?? 0) === 0 ? 'No jobs yet.' : 'No jobs match these filters.'} cta={(c?.all ?? 0) === 0 ? 'New job' : undefined} onCta={() => navigate({ to: '/jobs/new', search: { contact: undefined } })} /></Card>
      ) : (
        <Table
          cols={gridCols}
          // Wider layouts need more room than the fixed 720 the table assumes, or the extra
          // columns crush each other instead of scrolling.
          minW={Math.max(720, 320 + columns.length * 150)}
          header={<><span>{BASE_COLUMN.label}</span>{columns.map((c) => (
            <span key={c.key} className={c.align === 'right' ? 'text-right' : undefined}>{c.label}</span>
          ))}</>}
        >
          {(drafts.data ?? []).map((d) => (
            <Trow key={`draft-${d.id}`} cols={gridCols} onClick={() => navigate({ to: '/jobs/$id/edit', params: { id: String(d.id) } })}>
              <span className="min-w-0">
                <span className="block truncate font-bold text-token-primary">
                  {d.name || 'Untitled job'}
                  <span className="ml-2 rounded-full bg-canvas-subtle px-2 py-0.5 align-middle text-[11px] font-semibold uppercase tracking-[0.04em] text-token-secondary">
                    Draft
                  </span>
                </span>
                {rowAddress(d) && <span className="block truncate text-[12px] text-token-secondary">{rowAddress(d)}</span>}
              </span>
              {columns.map((c) => (
                <span key={c.key} className={c.align === 'right' ? 'flex justify-end text-[12px] text-token-muted' : 'truncate text-token-secondary'}>
                  {c.key === 'status'
                    ? 'Not saved yet'
                    : c.key === 'scheduled' && d.draft_updated_at
                    ? `Edited ${fmtDate(d.draft_updated_at)}`
                    : '—'}
                </span>
              ))}
            </Trow>
          ))}
          {rows.map((j, i) => (
            <Trow key={j.id} cols={gridCols} last={i === rows.length - 1} onClick={() => navigate({ to: '/jobs/$id', params: { id: String(j.id) } })}>
              <span className="min-w-0">
                <span className="block truncate font-bold text-token-primary">{j.name || 'Untitled job'}</span>
                {/* The address rides under the name only when it is NOT its own column, so turning
                    Address on moves it rather than printing it twice. */}
                {!columns.some((c) => c.key === 'address') && rowAddress(j) && (
                  <span className="block truncate text-[12px] text-token-secondary">{rowAddress(j)}</span>
                )}
              </span>
              {columns.map((c) => (
                <span key={c.key} className={c.align === 'right' ? 'flex justify-end' : 'truncate text-token-secondary'}>
                  {cellValue(c, j, contactName, userName)}
                </span>
              ))}
            </Trow>
          ))}
          <Pagination page={page} pageSize={PAGE} total={jobs.data?.count ?? 0} onPage={setPage} />
        </Table>
      )}
      </div>
    </Page>
  )
}

function JobWorkspacePreview({
  job, clientName, userName, tab, onTab, onOpen, onEdit, railCollapsed, onToggleRail, isLoading, isError, hasSelection, onRetry,
}: {
  job: JobRow | null
  clientName: (id: string | null) => string
  userName: (id: string | null) => string
  tab: string
  onTab: (tab: string) => void
  onOpen: () => void
  onEdit: () => void
  railCollapsed: boolean
  onToggleRail: () => void
  isLoading: boolean
  isError: boolean
  hasSelection: boolean
  onRetry: () => void
}) {
  if (isLoading) {
    return <div className="px-6 py-5" role="status" aria-live="polite" aria-label="Loading selected job"><SkeletonRows count={7} /></div>
  }
  if (isError) {
    return <PreviewState title="Job details could not be loaded" message="Retry loading this job. Your filters and queue position are still here." action="Retry" onAction={onRetry} />
  }
  if (!job) {
    if (hasSelection) return <PreviewState title="Job is unavailable" message="This job may have been deleted or you may no longer have access to it." />
    return (
      <div className="flex min-h-full flex-col items-center justify-center px-8 text-center">
        <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-[12px] bg-token-selected text-brand"><ArrowUpRight className="h-5 w-5" /></span>
        <h2 className="font-display text-[17px] font-semibold text-token-primary">Select a job</h2>
        <p className="mt-2 max-w-sm text-[13px] leading-relaxed text-token-secondary">Choose a job from the queue to review its schedule, customer and next actions without losing your filters.</p>
      </div>
    )
  }

  return <JobWorkspaceInspector
    job={job}
    clientName={clientName}
    userName={userName}
    tab={tab}
    onTab={onTab}
    onOpen={onOpen}
    onEdit={onEdit}
    railCollapsed={railCollapsed}
    onToggleRail={onToggleRail}
  />
}

function JobListError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-5 py-12 text-center" role="alert">
      <p className="text-[13px] font-medium text-token-primary">Jobs could not be loaded.</p>
      <p className="max-w-sm text-[12.5px] text-token-secondary">Check your connection and try again. Your current filters have not been changed.</p>
      <button className={btnSecondary} onClick={onRetry}>Retry</button>
    </div>
  )
}

function PreviewState({ title, message, action, onAction }: { title: string; message: string; action?: string; onAction?: () => void }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-8 text-center" role="status" aria-live="polite">
      <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-[12px] bg-token-selected text-brand"><ArrowUpRight className="h-5 w-5" /></span>
      <h2 className="font-display text-[17px] font-semibold text-token-primary">{title}</h2>
      <p className="mt-2 max-w-sm text-[13px] leading-relaxed text-token-secondary">{message}</p>
      {action && onAction && <button className={`${btnSecondary} mt-4`} onClick={onAction}>{action}</button>}
    </div>
  )
}

function JobWorkspaceInspector({
  job, clientName, userName, tab, onTab, onOpen, onEdit, railCollapsed, onToggleRail,
}: {
  job: JobRow
  clientName: (id: string | null) => string
  userName: (id: string | null) => string
  tab: string
  onTab: (tab: string) => void
  onOpen: () => void
  onEdit: () => void
  railCollapsed: boolean
  onToggleRail: () => void
}) {
  const time = useJobTime(job.id)
  const materials = useTaskMaterials(job.id)
  const notifications = useJobNotifications(job.id)
  const files = useQuery({
    queryKey: ['job-workspace-files', job.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_attachment')
        .select('field_id, kind, file_name, mime_type, storage_path, source_url')
        .eq('task_id', job.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as Array<{ field_id: string | null; kind: string; file_name: string | null; mime_type: string | null; storage_path: string | null; source_url: string | null }>
    },
  })
  const timeEntries = time.entries.data ?? []
  const trackedTime = timeEntries.reduce((total, entry) => total + entryMinutes(entry), 0)
  const notificationItems = notifications.data ?? []
  const jobFiles = files.data ?? []

  const address = rowAddress(job)
  const client = clientName(job.contact_id)
  const assigned = job.user_id ? userName(job.user_id) : 'Unassigned'
  const facts = [
    { label: 'Schedule', value: fmtDate(job.scheduled_date), icon: CalendarClock },
    { label: 'Customer', value: client === '—' ? 'No client linked' : client, icon: UserRound },
    { label: 'Assigned', value: assigned, icon: UserRound },
    { label: 'Address', value: address || 'No address added', icon: MapPin },
  ]

  return (
    <article className="min-h-full" aria-labelledby="job-workspace-preview-title">
      <p className="sr-only" aria-live="polite">Showing {job.name || 'untitled job'}.</p>
      <header className="border-b border-token-subtle px-6 pb-4 pt-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Job #{job.id}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2.5">
              <h2 id="job-workspace-preview-title" className="font-display truncate text-[20px] font-semibold tracking-[-0.02em] text-token-primary">{job.name || 'Untitled job'}</h2>
              <StatusPill status={job.status || 'pending'} />
            </div>
          </div>
          <div className="flex flex-none items-center gap-2">
            <button className={btnGhost} onClick={onToggleRail} aria-expanded={!railCollapsed}>{railCollapsed ? 'Show jobs' : 'Hide jobs'}</button>
            <button className={btnSecondary} onClick={onEdit}>Edit</button>
            <button className={btnPrimary} onClick={onOpen}>Open job record <ArrowUpRight className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      </header>

      <div className="grid gap-px border-b border-token-subtle bg-token-subtle sm:grid-cols-2 xl:grid-cols-4">
        {facts.map(({ label, value, icon: Icon }) => (
          <div key={label} className="min-w-0 bg-token-surface px-5 py-3.5">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-token-muted"><Icon className="h-3.5 w-3.5" /> {label}</span>
            <p className="mt-1 truncate text-[13px] font-medium text-token-primary" title={value}>{value}</p>
          </div>
        ))}
      </div>

      <div className="border-b border-token-subtle px-6 py-3">
        <TabPills active={tab} onChange={onTab} chips={[{ id: 'overview', label: 'Overview' }, { id: 'work', label: 'Work' }, { id: 'activity', label: 'Activity' }, { id: 'files', label: 'Files' }]} />
      </div>

      <div className="px-6 py-5" aria-live="polite">
        {tab === 'overview' && (
          <div className="max-w-3xl space-y-5">
            <section>
              <h3 className="font-display text-[15px] font-semibold text-token-primary">Job context</h3>
              <dl className="mt-3 divide-y divide-token-subtle rounded-[12px] border border-token">
                <div className="flex items-start justify-between gap-6 px-4 py-3"><dt className="text-[13px] text-token-secondary">Customer</dt><dd className="text-right text-[13px] font-medium text-token-primary">{client === '—' ? 'No client linked' : client}</dd></div>
                <div className="flex items-start justify-between gap-6 px-4 py-3"><dt className="text-[13px] text-token-secondary">Site</dt><dd className="max-w-[70%] text-right text-[13px] font-medium text-token-primary">{address || 'No address added'}</dd></div>
                <div className="flex items-start justify-between gap-6 px-4 py-3"><dt className="text-[13px] text-token-secondary">Scheduled</dt><dd className="text-right text-[13px] font-medium text-token-primary">{fmtDate(job.scheduled_date)}</dd></div>
              </dl>
            </section>
            <section className="grid gap-px overflow-hidden rounded-[12px] border border-token bg-token-subtle sm:grid-cols-3">
              <div className="bg-token-surface px-4 py-3.5"><p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-token-muted">Tracked time</p><p className="mt-1 text-[16px] font-semibold text-token-primary">{time.entries.isLoading ? 'Loading…' : fmtHours(trackedTime)}</p></div>
              <div className="bg-token-surface px-4 py-3.5"><p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-token-muted">Materials</p><p className="mt-1 text-[16px] font-semibold text-token-primary">{materials.isLoading ? 'Loading…' : `${materials.materials.length} logged`}</p></div>
              <div className="bg-token-surface px-4 py-3.5"><p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-token-muted">Files</p><p className="mt-1 text-[16px] font-semibold text-token-primary">{files.isLoading ? 'Loading…' : `${jobFiles.length} attached`}</p></div>
            </section>
          </div>
        )}
        {tab === 'work' && (
          <div className="max-w-3xl space-y-5">
            <section className="rounded-[12px] border border-token">
              <div className="flex items-center justify-between border-b border-token-subtle px-4 py-3"><h3 className="flex items-center gap-2 text-[13px] font-semibold text-token-primary"><Clock3 className="h-4 w-4 text-token-muted" /> Time</h3><span className="text-[12px] text-token-secondary">{time.entries.isLoading ? 'Loading…' : fmtHours(trackedTime)}</span></div>
              {time.entries.isLoading ? <SkeletonRows count={3} /> : timeEntries.length === 0 ? <PreviewEmpty message="No time has been tracked on this job." /> : <div className="divide-y divide-token-subtle">{timeEntries.slice(0, 6).map((entry) => <div key={entry.id} className="flex items-center justify-between gap-4 px-4 py-3"><span className="min-w-0"><span className="block text-[13px] font-medium text-token-primary">{entry.notes || 'Time entry'}</span><span className="mt-0.5 block text-[12px] text-token-secondary">{phFormat(new Date(entry.started_at), 'datetime', { dateStyle: 'medium', timeStyle: 'short' })}</span></span><span className="flex-none text-[13px] font-semibold tabular-nums text-token-primary">{fmtHours(entryMinutes(entry))}</span></div>)}</div>}
            </section>
            <section className="rounded-[12px] border border-token">
              <div className="flex items-center justify-between border-b border-token-subtle px-4 py-3"><h3 className="flex items-center gap-2 text-[13px] font-semibold text-token-primary"><Package className="h-4 w-4 text-token-muted" /> Materials</h3><span className="text-[12px] text-token-secondary">{materials.materials.length} logged</span></div>
              {materials.isLoading ? <SkeletonRows count={2} /> : materials.materials.length === 0 ? <PreviewEmpty message="No materials have been logged on this job." /> : <div className="divide-y divide-token-subtle">{materials.materials.slice(0, 6).map((material) => <div key={material.id} className="flex items-center justify-between gap-4 px-4 py-3"><span className="text-[13px] font-medium text-token-primary">{material.item?.name || 'Inventory item'}</span><span className="text-[13px] text-token-secondary">{material.qty} {material.item?.unit || 'units'}</span></div>)}</div>}
            </section>
          </div>
        )}
        {tab === 'activity' && (
          <section className="max-w-3xl rounded-[12px] border border-token">
            <div className="border-b border-token-subtle px-4 py-3"><h3 className="text-[13px] font-semibold text-token-primary">Customer communications</h3><p className="mt-0.5 text-[12px] text-token-secondary">Scheduled and sent notifications for this job.</p></div>
            {notifications.isLoading ? <SkeletonRows count={3} /> : notifications.isError ? <PreviewEmpty message="Activity could not be loaded. Open the full job to retry." /> : notificationItems.length === 0 ? <PreviewEmpty message="No customer communications are recorded for this job." /> : <div className="divide-y divide-token-subtle">{notificationItems.slice(0, 8).map((item) => <div key={`${item.ruleId}-${item.at}`} className="flex items-start justify-between gap-4 px-4 py-3"><span className="min-w-0"><span className="block text-[13px] font-medium text-token-primary">{item.name}</span>{item.reason && <span className="mt-0.5 block text-[12px] text-token-secondary">{item.reason}</span>}</span><span className="flex-none text-right text-[12px] text-token-secondary"><span className="block capitalize">{item.status}</span>{item.at && <span className="mt-0.5 block text-token-muted">{phFormat(new Date(item.at), 'date', { day: 'numeric', month: 'short' })}</span>}</span></div>)}</div>}
          </section>
        )}
        {tab === 'files' && (
          <section className="max-w-3xl rounded-[12px] border border-token">
            <div className="border-b border-token-subtle px-4 py-3"><h3 className="text-[13px] font-semibold text-token-primary">Files and photos</h3><p className="mt-0.5 text-[12px] text-token-secondary">Attachments saved against this job and its form.</p></div>
            {files.isLoading ? <SkeletonRows count={3} /> : files.isError ? <PreviewEmpty message="Files could not be loaded. Open the full job to retry." /> : jobFiles.length === 0 ? <PreviewEmpty message="No files or photos are attached to this job." /> : <div className="divide-y divide-token-subtle">{jobFiles.slice(0, 12).map((file, index) => <a key={`${file.storage_path ?? file.file_name ?? file.kind}-${index}`} href={file.source_url ?? undefined} target={file.source_url ? '_blank' : undefined} rel="noreferrer" className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-token-hover"><span className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-token-hover text-token-secondary"><FileText className="h-4 w-4" /></span><span className="min-w-0"><span className="block truncate text-[13px] font-medium text-token-primary">{file.file_name || `${file.kind} attachment`}</span><span className="mt-0.5 block text-[12px] capitalize text-token-secondary">{file.mime_type || file.kind}</span></span></a>)}</div>}
          </section>
        )}
        <div className="mt-5 rounded-[12px] border border-token bg-canvas-subtle px-4 py-3.5">
          <h3 className="text-[13px] font-semibold text-token-primary">Continue in the full job</h3>
          <p className="mt-1 text-[12.5px] leading-relaxed text-token-secondary">Use the full job record for safety checks, status changes, financial actions and complete job forms.</p>
          <button className={`${btnSecondary} mt-3`} onClick={onOpen}>Open job record</button>
        </div>
      </div>
    </article>
  )
}

function PreviewEmpty({ message }: { message: string }) {
  return (
    <div className="px-4 py-8 text-center">
      <p className="text-[13px] text-token-secondary">{message}</p>
    </div>
  )
}

/** One cell. Kept out of the row so the row stays readable and each column has one obvious home. */
function cellValue(
  c: JobColumn,
  j: JobRow,
  contactName: (id: string | null) => string,
  userName: (id: string | null) => string,
): React.ReactNode {
  if (c.fieldId) {
    const v = (j.task_field_value ?? []).find((r) => r.field_id === c.fieldId)?.value_text
    return v?.trim() ? v : '—'
  }
  switch (c.key) {
    case 'client': return contactName(j.contact_id)
    case 'assigned': return userName(j.user_id)
    case 'scheduled': return fmtDate(j.scheduled_date)
    case 'finishes': return fmtDate(j.finishes ?? null)
    case 'created': return fmtDate(j.created_at ?? null)
    case 'address': return rowAddress(j) || '—'
    case 'number': return `#${j.id}`
    case 'status': return <StatusPill status={j.status || 'pending'} />
    default: return '—'
  }
}

function ColumnPicker({ columns, custom, onChange, onReset, onClose }: {
  columns: JobColumn[]
  custom: JobColumn[]
  onChange: (keys: string[]) => void
  onReset: () => void
  onClose: () => void
}) {
  const active = columns.map((c) => c.key)
  const isDefault = active.length === DEFAULT_KEYS.length && DEFAULT_KEYS.every((k) => active.includes(k))
  const toggle = (key: string) => {
    // Order follows the offered order rather than click order, so turning a column off and back on
    // puts it back where it was instead of appending it to the end.
    const next = active.includes(key) ? active.filter((k) => k !== key) : [...active, key]
    const order = [...BUILTIN_COLUMNS.map((c) => c.key), ...custom.map((c) => c.key)]
    onChange(order.filter((k) => next.includes(k)))
  }
  const Group = ({ title, items }: { title: string; items: JobColumn[] }) => (
    items.length === 0 ? null : (
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">{title}</p>
        <div className="flex flex-wrap gap-2">
          {items.map((c) => {
            const on = active.includes(c.key)
            return (
              <button
                key={c.key}
                onClick={() => toggle(c.key)}
                aria-pressed={on}
                className={'rounded-full border px-3.5 py-1.5 text-[13px] transition-colors '
                  + (on
                    ? 'border-token-strong bg-brand font-semibold text-brand-fg hover:bg-brand'
                    : 'border-token-subtle bg-token-surface text-token-primary hover:border-token-subtle hover:bg-token-surface')}
              >
                {c.label}
              </button>
            )
          })}
        </div>
      </div>
    )
  )
  return (
    <Card className="mb-4">
      {/* Header sits on its own divided row, like CardHeader elsewhere, so the panel reads as part
          of the page furniture rather than text dropped on a white rectangle. */}
      <header className="flex items-center justify-between border-b border-token-subtle px-5 py-3.5">
        <div>
          <h2 className="font-display text-[15px] font-semibold text-token-primary">Columns</h2>
          <p className="mt-0.5 text-[12px] text-token-secondary">Job always shows. Saved in this browser, just for you.</p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close columns"
          className="-mr-1.5 flex h-8 w-8 flex-none items-center justify-center rounded-full text-token-muted transition-colors hover:bg-canvas-subtle hover:text-token-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="flex flex-col gap-4 p-5">
        <Group title="Job details" items={BUILTIN_COLUMNS} />
        <Group title="Your job form" items={custom} />
      </div>
      {/* Reset is a footer action, not a stray underlined link mid-panel. Hidden when the layout
          already IS the default, because a reset that does nothing is just a puzzle. */}
      {!isDefault && (
        <footer className="flex justify-end border-t border-token-subtle px-5 py-3">
          <button
            onClick={onReset}
            className="rounded-[9px] border border-token-subtle px-3 py-1.5 text-[12.5px] font-semibold text-token-secondary transition-colors hover:border-token-subtle hover:bg-token-surface hover:text-token-primary"
          >
            Reset to default
          </button>
        </footer>
      )}
    </Card>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">{label}</span>{children}</label>
}
